import Vue from 'vue'
import { devLog } from '@/utils'
import { Loading, Notify, QSpinnerInfinity } from 'quasar'
import ptiMixin from '@/utils/pti/ptiMixin'
import { TYPE_AGENT_FALL } from '@/consts/events'
import { $ui, dialogConfirm } from '@/utils/ui'
import { SERVICE_POST_EVENT_PTI } from '@/store/modules/service/action-types'
import {
  FALL_CALIBRATION_EXPECTED_IMMOBILITY_TIMES,
  FALL_DETECTION_BETA,
  FALL_DETECTION_FALL_CANCEL,
  FALL_DETECTION_MIN_DATE_DIFF,
  FALL_DETECTION_SPEED_WALK,
  FALL_STATUS_ACTIVE,
  FALL_STATUS_CALIBRATION,
  FALL_STATUS_DESKTOP,
  FALL_STATUS_FALL,
  FALL_STATUS_FALSE_CONFIRMATION,
  FALL_STATUS_INACTIVE,
  FALL_STATUS_LIST,
  FALL_STATUS_RAS,
  FALL_STATUS_SENDING,
  FALL_STATUS_VERTICAL,
  FALL_STATUS_WAIT_MIN_ANGLE,
  FALL_STATUS_WAIT_WAKE,
  FALL_STATUS_WAKE,
  FALL_STATUS_WALK
} from '@/consts/pti/fallDetector'
import { moveToForeground } from '@/utils/navigator/backgroundTask'
import { getVibrateTimeout, vibrate } from '@/utils/io/vibrate'
import { checkIfFalling, checkIfWakeUp, checkIfWalking } from '@/utils/pti/utils'

const { cordova } = window
const NOTIFICATION_ID = 1

export default new Vue({
  name: 'FallDetector',
  eventSettingsCheckKey: 'ptiCheckFall',
  statusList: FALL_STATUS_LIST,
  mixins: [ptiMixin],
  data () {
    return {
      wakeTimeout: null,
      horizontalTimes: 0,
      immobilityTimes: 0,
      fallDate: null,
      immobilityInterval: null,
      zAxis: [],
      zAxisCalibration: [],
      onDeviceMotionZAxisTick: 0
    }
  },
  computed: {
    minHorizontalAndMoveTimes () {
      return Math.min(FALL_CALIBRATION_EXPECTED_IMMOBILITY_TIMES, this.horizontalTimes, this.immobilityTimes)
    },
    isIos () {
      return this.$q.platform.is.ios
    }
  },
  watch: {
    status: {
      immediate: true,
      async handler (status) {
        switch (status) {
          case FALL_STATUS_INACTIVE.value: {
            this._handleWindowEvent('compassneedscalibration', 'onCompassNeedsCalibration', false)
            this._handleWindowEvent('devicemotion', 'onDeviceMotion', false)
            this._handleWindowEvent('deviceorientation', 'onDeviceOrientation', false)
            break
          }
          case FALL_STATUS_ACTIVE.value: {
            this.wakeTimeout = null
            if (typeof this.closeNotify === 'function') {
              this.closeNotify()
            }
            this.closeNotify = null
            this._handleWindowEvent('compassneedscalibration', 'onCompassNeedsCalibration')
            this._handleWindowEvent('devicemotion', 'onDeviceMotion')
            this._handleWindowEvent('deviceorientation', 'onDeviceOrientation')
            break
          }
          case FALL_STATUS_CALIBRATION.value: {
            if (typeof navigator.vibrate !== 'function') {
              devLog('Calibration indisponible')
              this.reset()
              break
            }
            this.notifyCalibration = Notify.create({
              message: 'Veuillez poser votre téléphone sur une surface plane pour débuter la calibration',
              timeout: 0,
              color: 'info'
            })
            this.horizontalTimes = 0
            this.immobilityTimes = 0
            this._handleWindowEvent('compassneedscalibration', 'onCompassNeedsCalibration')
            this._handleWindowEvent('devicemotion', 'onDeviceMotion')
            this._handleWindowEvent('deviceorientation', 'onDeviceOrientation')
            this.immobilityInterval = setInterval(() => {
              ++this.horizontalTimes
              ++this.immobilityTimes
            }, 200)
            break
          }
          case FALL_STATUS_FALL.value: {
            this.wakeTimeout = new Date().getTime() + (3 + FALL_DETECTION_FALL_CANCEL) * 1000
            this.timeoutFall = setTimeout(() => {
              devLog(`Vérification faux positif`)
              this.status = FALL_STATUS_FALSE_CONFIRMATION.value
            }, 3000)
            break
          }
          case FALL_STATUS_FALSE_CONFIRMATION.value: {
            this.timeoutFall = setTimeout(() => {
              if (this.status === FALL_STATUS_FALSE_CONFIRMATION.value) {
                devLog(`En attente d'une tentative pour se relever`)
                this.status = FALL_STATUS_WAIT_WAKE.value
                this.alarmPlay({ loop: true })
              }
            }, 3000)
            break
          }
          case FALL_STATUS_RAS.value:
          case FALL_STATUS_VERTICAL.value:
          case FALL_STATUS_WALK.value:
          case FALL_STATUS_WAKE.value: {
            devLog('Réactivation du PTI')
            if (cordova) {
              cordova.plugins.notification.local.clear([NOTIFICATION_ID])
            }
            this.reset()
            break
          }
          case FALL_STATUS_SENDING.value: {
            try {
              this.alarmStop()
              Notify.create({
                message: 'Un signalement de chute à été transmis.',
                caption: 'Veuillez contacter votre agence au plus vite',
                color: 'negative',
                timeout: 0,
                multiLine: true,
                actions: [{
                  label: 'Fermer',
                  color: 'white'
                }]
              })

              await this.$store.dispatch(
                'service/' + SERVICE_POST_EVENT_PTI,
                {
                  type: this.$store.getters.constServiceEventTypesKeys[TYPE_AGENT_FALL.typeKey]
                }
              )
            } catch (e) {
              devLog('error FALL_STATUS_SENDING ', e)
            }
            this.reset()
            break
          }
        }
      }
    },
    async minHorizontalAndMoveTimes (times) {
      if (times === FALL_CALIBRATION_EXPECTED_IMMOBILITY_TIMES) {
        devLog('Début de la calibration.')
        if (typeof this.notifyCalibration === 'function') {
          this.notifyCalibration()
        }
        this.$q.notify({
          message: 'Calibration en cours...',
          caption: 'Veuillez laisser le téléphone immobile',
          multiLine: true,
          timeout: getVibrateTimeout(),
          color: 'negative'
        })
        await vibrate()
        devLog('Calibration terminée')
        this.$q.notify({
          message: 'Calibration terminée',
          color: 'positive'
        })
        clearInterval(this.immobilityInterval)
        this.reset()
      }
    }
  },
  methods: {
    _onActiveStateChange () {
      this.reset()
    },
    _handleWindowEvent (eventName, methodName, status = true) {
      window.removeEventListener(eventName, this[methodName], true)
      if (status) {
        window.addEventListener(eventName, this[methodName], true)
      }
    },
    _fallWakeUp (isWakeUp = false) {
      if (isWakeUp) {
        devLog(`L'agent s'est relevé`)
        this.setWake(FALL_STATUS_WAKE)
        return true
      }
      return false
    },
    _fallWalking ({
      x,
      y
    }) {
      if (checkIfWalking(x, y, FALL_DETECTION_SPEED_WALK)) {
        devLog(`L'agent marche`)
        this.setWake(FALL_STATUS_WALK)
        return true
      }
      return false
    },
    _fallSignal () {
      const signal = (new Date().getTime()) >= this.wakeTimeout
      if (signal) {
        this.status = FALL_STATUS_SENDING.value
      }

      this.notifyFall(signal)
    },
    _getAbsAcc (acceleration) {
      return ['x', 'y', 'z'].map((k) => Math.abs(acceleration[k]))
    },
    _calibrationIsStable (acceleration) {
      if (
        Math.max(...this._getAbsAcc(acceleration)) > 0.5 &&
        this.minHorizontalAndMoveTimes < FALL_CALIBRATION_EXPECTED_IMMOBILITY_TIMES
      ) {
        this.immobilityTimes = 0
        return true
      }
      return false
    },
    setWake (reason) {
      clearTimeout(this.timeoutFall)
      if (this.status === FALL_STATUS_FALSE_CONFIRMATION.value) {
        this.status = FALL_STATUS_ACTIVE.value
      }
      if (this.status === FALL_STATUS_WAIT_WAKE.value) {
        this.status = reason.value
      }
    },
    reset () {
      const { isActive } = this
      if (this.alarmPlayer) this.alarmStop()

      if (!(this.$q.platform.is.mobile || $ui.isDev)) {
        this.status = FALL_STATUS_DESKTOP.value
        return
      }
      this.status = isActive
        ? FALL_STATUS_ACTIVE.value
        : FALL_STATUS_INACTIVE.value
    },
    zCalibration () {
      dialogConfirm(`Si votre téléphone ne vibre pas pendant la calibration, merci d'activer le vibreur de votre téléphone.`)
        .onOk(() => {
          this.status = FALL_STATUS_CALIBRATION.value
        })
    },
    notifyFall (signal) {
      const message = 'Chute détectée'
      const caption = 'Sans interaction de votre part, une alerte sera transmise.'
      if (cordova) {
        const timeDiff = Math.abs(new Date().getTime() - this.wakeTimeout)
        const datePercent = Math.floor(timeDiff / 100)
        cordova.plugins.notification.local.schedule({
          id: NOTIFICATION_ID,
          color: '#FF0000',
          sound: null,
          title: signal
            ? 'Chute signalée'
            : `Signalement de chute`,
          text: `${message}\n${caption}`,
          progressBar: { value: datePercent }
        })
      }
      if (!this.closeNotify) {
        if (cordova) moveToForeground()
        this.closeNotify = Notify.create({
          message,
          caption,
          timeout: FALL_DETECTION_FALL_CANCEL * 1000,
          position: 'center',
          progress: true,
          color: 'negative',
          multiLine: true,
          actions: [
            {
              label: 'R.A.S',
              color: 'white',
              handler: () => {
                this.setWake(FALL_STATUS_RAS)
              }
            }
          ]
        })
      }
    },
    onCompassNeedsCalibration (e) {
      Loading.show({
        message: '<div class="text-h6">Calibration requise</div>Pour assurer la calibration de votre appareil, merci de décrire un "huit" avec votre téléphone.',
        spinner: QSpinnerInfinity
      })
      const numberOfHeight = [0, 0, 0]
      const _hide = ({ acceleration = {} }) => {
        ['x', 'y', 'z'].forEach((k, i) => {
          if (numberOfHeight[i] >= 5) return
          if (acceleration[k] < -5 || acceleration[k] > 5) {
            numberOfHeight[i]++
          }
        })
        if (numberOfHeight.every((i) => i >= 5)) {
          devLog('Fin de la calibration')
          window.removeEventListener('devicemotion', _hide, true)
          Loading.hide()
        }
        // Loading.hide()
      }
      window.addEventListener('devicemotion', _hide, true)
      e.preventDefault()
    },
    onDeviceOrientation ({
      beta,
      gamma
    }) {
      switch (this.status) {
        case FALL_STATUS_WAIT_WAKE.value: {
          const absBeta = Math.abs(beta)
          if (
            absBeta > 90 - FALL_DETECTION_BETA &&
            absBeta < 90 + FALL_DETECTION_BETA) {
            devLog(`L'agent a regagné sa verticalité`, beta)
            this.setWake(FALL_STATUS_VERTICAL)
          }
          break
        }
        case FALL_STATUS_CALIBRATION.value: {
          const maxAngle = Math.max(Math.abs(beta), Math.abs(gamma))
          if (
            maxAngle > FALL_STATUS_WAIT_MIN_ANGLE &&
            this.minHorizontalAndMoveTimes < FALL_CALIBRATION_EXPECTED_IMMOBILITY_TIMES
          ) {
            this.horizontalTimes = 0
            break
          }
          break
        }
      }
    },
    onDeviceMotion ({ acceleration }) {
      switch (this.status) {
        case FALL_STATUS_ACTIVE.value: {
          const _date = new Date().getTime()
          const _isFall = this.fallDate !== null ? (_date - this.fallDate) > FALL_DETECTION_MIN_DATE_DIFF : false

          if (checkIfFalling(acceleration.z)) {
            if (this.fallDate === null) {
              this.fallDate = new Date().getTime()
            }

            if (_isFall) {
              this.status = FALL_STATUS_FALL.value
              this.fallDate = null
            }
            break
          }

          if (_isFall && this.status === FALL_STATUS_ACTIVE.value) {
            this.fallDate = null
          }
          break
        }
        case FALL_STATUS_CALIBRATION.value: {
          if (typeof navigator.vibrate !== 'function') {
            break
          }

          if (this._calibrationIsStable(acceleration)) break
          break
        }
        case FALL_STATUS_FALSE_CONFIRMATION.value:
        case FALL_STATUS_WAIT_WAKE.value: {
          const isWakeUp = checkIfWakeUp(acceleration.z)

          if (this._fallWakeUp(isWakeUp)) {
            break
          }

          if (this._fallWalking(acceleration)) {
            break
          }

          if (this.status !== FALL_STATUS_WAIT_WAKE.value) {
            break
          }

          this._fallSignal()
          break
        }
      }

      this.zAxis.push(acceleration.z)
      if (this.zAxis.length >= 100) {
        this.zAxis.shift()
      }
    }
  }
})
